import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import KpiCard from '@/components/base/KpiCard';
import ProgressBar from '@/components/base/ProgressBar';
import StatusBadge from '@/components/base/StatusBadge';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';

const openStatuses = ['Onaylandı', 'Kaybedildi', 'İptal'];

const funnelStages = [
  { label: 'Taslak', tone: 'bg-secondary-300' },
  { label: 'Hazırlanıyor', tone: 'bg-secondary-400' },
  { label: 'Gönderildi', tone: 'bg-primary-400' },
  { label: 'Görüşmede', tone: 'bg-primary-500' },
  { label: 'Revize', tone: 'bg-accent-400' },
  { label: 'Onaylandı', tone: 'bg-primary-600' },
  { label: 'Kaybedildi', tone: 'bg-secondary-500' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { quotes, projects, receivables, collections, payments, projectFinances, calendarEvents, notifications, customerName } = useData();

  const openQuotes = quotes.filter((q) => !openStatuses.includes(q.status));
  const openQuoteAmount = openQuotes.reduce((s, q) => s + q.amount, 0);
  const wonCount = quotes.filter((q) => q.status === 'Onaylandı').length;
  const activeProjects = projects.filter((p) => p.status !== 'Tamamlandı');
  const nearDelivery = projects.filter((p) => p.status === 'Teslimi Yaklaşıyor').length;
  const overdueProjects = projects.filter((p) => p.status === 'Gecikti').length;
  const pendingCollection = receivables.reduce((s, r) => s + r.remaining, 0);
  const collectedMonth = collections
    .filter((c) => c.date >= '2026-08-01')
    .reduce((s, c) => s + c.amount, 0);
  const paidMonth = payments.filter((p) => p.date >= '2026-08-01').reduce((s, p) => s + p.amount, 0);
  const estimatedProfit = projectFinances
    .filter((pf) => activeProjects.some((p) => p.id === pf.projectId))
    .reduce((s, pf) => s + pf.grossProfit, 0);

  const funnelData = funnelStages.map((stage) => {
    const stageQuotes = quotes.filter((q) => q.status === stage.label);
    return {
      ...stage,
      count: stageQuotes.length,
      amount: stageQuotes.reduce((s, q) => s + q.amount, 0),
    };
  });

  const upcoming = calendarEvents.filter((e) => e.date >= '2026-08-12').slice(0, 6);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Genel Bakış"
        description="Şirketinizin güncel durumuna hızlı bir bakış."
      />

      <div className="stagger grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard label="Açık Teklifler" value={String(openQuotes.length)} sub="8 aktif takip" icon="ri-file-list-3-line" tone="primary" onClick={() => navigate('/quotes')} />
        <KpiCard label="Açık Teklif Tutarı" value={formatTL(openQuoteAmount)} icon="ri-price-tag-3-line" tone="primary" onClick={() => navigate('/quotes')} />
        <KpiCard label="Bu Ay Kazanılan İş" value={String(wonCount)} sub="onaylanan teklif" icon="ri-check-double-line" tone="accent" onClick={() => navigate('/projects')} />
        <KpiCard label="Devam Eden Projeler" value={String(activeProjects.length)} icon="ri-briefcase-line" tone="primary" onClick={() => navigate('/projects')} />
        <KpiCard label="Teslimi Yaklaşanlar" value={String(nearDelivery)} sub="7 gün içinde" icon="ri-alarm-line" tone="accent" onClick={() => navigate('/projects')} />
        <KpiCard label="Geciken Projeler" value={String(overdueProjects)} sub="müdahale gerekli" icon="ri-error-warning-line" tone="accent" onClick={() => navigate('/projects')} />
        <KpiCard label="Bekleyen Tahsilat" value={formatTL(pendingCollection)} icon="ri-bank-card-line" tone="primary" onClick={() => navigate('/finance')} />
        <KpiCard label="Bu Ay Tahsil Edilen" value={formatTL(collectedMonth)} icon="ri-money-dollar-circle-line" tone="accent" onClick={() => navigate('/finance')} />
        <KpiCard label="Bu Ay Yapılan Ödeme" value={formatTL(paidMonth)} icon="ri-arrow-up-circle-line" tone="secondary" onClick={() => navigate('/finance')} />
        <KpiCard label="Tahmini Proje Kârı" value={formatTL(estimatedProfit)} sub="aktif projeler" icon="ri-line-chart-line" tone="dark" onClick={() => navigate('/reports')} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Teklif Hunisi</h2>
              <button
                type="button"
                onClick={() => navigate('/quotes')}
                className="cursor-pointer text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                Tümünü gör
              </button>
            </div>
            <div className="grid grid-cols-7 gap-2 p-5">
              {funnelData.map((stage) => (
                <div key={stage.label} className="flex flex-col">
                  <div className={`h-1.5 rounded-full ${stage.tone}`} />
                  <p className="mt-2 text-center font-heading text-lg font-semibold text-foreground-950">{stage.count}</p>
                  <p className="text-center text-[11px] font-semibold text-foreground-500">{stage.label}</p>
                  <p className="mt-0.5 truncate text-center text-[11px] text-foreground-400">{formatTL(stage.amount)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Devam Eden Projeler</h2>
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="cursor-pointer text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                Tümünü gör
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Proje</th>
                    <th className="px-4 py-3">Müşteri</th>
                    <th className="px-4 py-3">Aşama</th>
                    <th className="px-4 py-3">İlerleme</th>
                    <th className="px-4 py-3">Teslim</th>
                    <th className="px-4 py-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {activeProjects.slice(0, 7).map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/projects/${p.id}`)}
                      className="cursor-pointer border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60"
                    >
                      <td className="px-5 py-3">
                        <p className="font-semibold text-foreground-900">{p.name}</p>
                        <p className="text-xs text-foreground-500">{p.id}</p>
                      </td>
                      <td className="px-4 py-3 text-foreground-700">{customerName(p.customerId)}</td>
                      <td className="px-4 py-3 text-foreground-600">{p.currentStage}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <ProgressBar value={p.progress} className="w-20" />
                          <span className="text-xs font-semibold text-foreground-600">%{p.progress}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600">{formatDate(p.plannedDelivery)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={p.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50">
            <div className="border-b border-background-200 px-5 py-4">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Kritik Uyarılar</h2>
            </div>
            <div className="divide-y divide-background-100">
              {notifications.slice(0, 5).map((n) => (
                <div key={n.id} className="flex items-start gap-3 px-5 py-3.5">
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${n.type === 'danger' ? 'bg-accent-100 text-accent-700' : n.type === 'warning' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'}`}>
                    <i className={n.type === 'danger' ? 'ri-error-warning-line' : n.type === 'warning' ? 'ri-alarm-warning-line' : 'ri-information-line'} />
                  </span>
                  <p className="text-sm leading-snug text-foreground-700">{n.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Yaklaşan İşler</h2>
              <button
                type="button"
                onClick={() => navigate('/calendar')}
                className="cursor-pointer text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                Takvim
              </button>
            </div>
            <div className="divide-y divide-background-100">
              {upcoming.map((e) => (
                <div key={e.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md bg-background-100">
                    <span className="text-sm font-bold leading-none text-foreground-900">{new Date(e.date).getDate()}</span>
                    <span className="text-[10px] font-semibold uppercase text-foreground-500">{new Date(e.date).toLocaleDateString('tr-TR', { month: 'short' })}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground-900">{e.title}</p>
                    <p className="truncate text-xs text-foreground-500">{e.related}</p>
                  </div>
                  <StatusBadge status={e.type} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}